
import { sortBy } from 'lodash';
import { Component, Emit, InjectReactive, Provide, Vue, Watch } from 'vue-property-decorator'
import { Getter, Mutation } from 'vuex-class';

import { MutationTypes, Views } from '@/enums';
import BasketModule from '@/store/modules/basket';
import UserModule from '@/store/modules/user';

const { SET_PREVIEW_LOCATION } = MutationTypes;

@Component
export default class Map extends Vue {
  @Getter allRestaurants!: Restaurant[];
  @Getter activeRestaurant!: Restaurant;
  @InjectReactive() viewContainerRef!: any;
  @InjectReactive() isMobile!: boolean;
  @Getter previewLocation!: Restaurant | null;
  @Mutation(SET_PREVIEW_LOCATION) setPreviewLocation: (location: Restaurant | null) => void;

  center: { lat: number; lng: number };
  defaultZoom = 16;
  zoom: number;
  mapRef: any;
  width: number;
  height: number;
  icon: string;
  id = 'google-map-component';

  @Provide() geolocation: {
    /**
     * Google Geocoder Objet
     * @type {Geocoder}
     * @link https://developers.google.com/maps/documentation/javascript/reference#Geocoder
     */
    geocoder?: {
      geocode: (options: any) => any[];
    };
  } = { };

  data() {
    return {
      center: { lat: 43.0389, lng: -87.9065 }, // default map center
      zoom: undefined,
      width: 640,
      height: 450,
      icon: 'https://cousinssubs.azurewebsites.net/CousinsSubsTheme/img/map_marker.png'
    }
  }

  @Emit('preview-location')
  showLocation(location) {
    return location;
  }

  get favoriteStores() {
    return sortBy(UserModule.favoriteRestaurants, ['isDefault']).reverse();
  }

  @Watch('previewLocation', { immediate: false })
  watchPreviewLocation(newVal) {
    if (newVal) {
      this.panToZoom({
        lat: newVal.latitude,
        lng: newVal.longitude,
      });
    } else if (this.userCoords && BasketModule.isDelivery) {
      this.panToZoom({
        lat: this.userCoords.latitude,
        lng: this.userCoords.longitude,
      });
    } else if (this.$route.name === Views.FAVORITE_LOCATIONS && this.favoriteStores?.length) {
      this.fitBounds(this.favoriteStores);
    } else if (this.activeRestaurant.id && !BasketModule.isDelivery) {
      this.panToZoom({
        lat: this.activeRestaurant.latitude,
        lng: this.activeRestaurant.longitude,
      });
    } else {
      this.fitBounds();
    }
  }

  @Watch('userCoords', { immediate: false })
  focusOnUserCoords({ latitude, longitude }) {
    if (
      BasketModule.isDelivery &&
      (latitude && longitude) &&
      !this.previewLocation
    ) {
      this.panToZoom({
        lat: latitude,
        lng: longitude,
      });
    }
  }

  @Watch('activeRestaurant', { immediate: false })
  watchActiveRestaurant(newActive) {
    if (!BasketModule.isDelivery) {
      if (newActive.id && !this.previewLocation) {
        this.panToZoom({
          lat: newActive.latitude,
          lng: newActive.longitude,
        });
      }
    } else if (this.userCoords && BasketModule.isDelivery) {
      this.panToZoom({
        lat: this.userCoords.latitude,
        lng: this.userCoords.longitude,
      });
    }
  }

  @Watch('allRestaurants', { immediate: false })
  watchAllRestaurants(newRestaurants, oldValOrOverride) {
    if (
      (!this.previewLocation &&
      !this.activeRestaurant.id) ||
      oldValOrOverride === true
    ) {
      if (newRestaurants.length === 1) {
        this.panToZoom({
          lat: newRestaurants[0].latitude,
          lng: newRestaurants[0].longitude,
        });
      } else if (newRestaurants.length) {
        this.fitBounds();
      }
    }
  }

  created() {
    window.addEventListener('resize', this.setWidth);
  }

  destroyed() {
    window.removeEventListener('resize', this.setWidth);
  }

  async mounted() {
    // At this point, the child GmapMap has been mounted, but
    // its map has not been initialized.
    // Therefore we need to write mapRef.$mapPromise.then(() => ...)\
    this.mapRef = this.$refs.mapRef;
    this.mapRef.$mapPromise.then((map) => { // eslint-disable-line
      this.watchPreviewLocation(this.previewLocation);
      this.panToZoom(this.center);
    })

    this.setWidth();
  }

  updated() {
    this.setWidth();
  }

  setWidth() {
    this.$nextTick(() => {
      this.width = (this.$refs.map_wrap as any).offsetWidth;
      this.height = (this.$refs.map_wrap as any).offsetHeight;
    })
  }

  fitBounds(locations?: FavoriteRestaurant[] | Restaurant[]) {
    if (window?.google?.maps) {
      const b = new window.google.maps.LatLngBounds();

      for (const rest of (locations || this.allRestaurants)) {
        b.extend({
          lat: rest.latitude,
          lng: rest.longitude
        });
      }

      this.mapRef.fitBounds(b);
    }
  }

  panToBounds() {
    if (window?.google?.maps) {
      const b = new window.google.maps.LatLngBounds();

      for (const rest of this.allRestaurants) {
        b.extend({
          lat: rest.latitude,
          lng: rest.longitude
        });
      }

      this.mapRef.panToBounds(b);
    }
  }

  panToZoom({ lat, lng }: { lat: number; lng: number }) {
    if (this.mapRef) {
      this.mapRef.panTo({ lat, lng });
      this.zoom = this.defaultZoom;
    }
  }

  panTo({ lat, lng }) {
    if (this.mapRef) {
      this.mapRef.panTo({ lat, lng });
    }
  }

  handleZoomChange(change) {
    if (change !== this.zoom && change !== this.defaultZoom) {
      this.zoom = change;
    }
  }

  handleMarkerClick(location) {
    if (this.previewLocation?.id === location.id) {
      this.setPreviewLocation(null);
    } else {
      this.setPreviewLocation(location);
    }
  }

  get userCoords() {
    const { coords, hasCoords } = UserModule.customerLocation;
    return hasCoords && coords;
  }

  get address() {
    const { building, streetAddress, city, zipCode } = BasketModule.current?.deliveryAddress || {};
    return `${streetAddress}${building ? ` ${building} ` : ' '}${city} ${zipCode}`;
  }

  get userMarker() {
    return BasketModule.isDelivery && this.userCoords ? {
      position: {
        lat: this.userCoords.latitude,
        lng: this.userCoords.longitude,
      }
    } : null
  }

  get markers() {
    return this.allRestaurants?.map((rest) => {
      return {
        ...rest,
        position: {
          lat: rest.latitude,
          lng: rest.longitude
        }
      }
    });
  }

  get useCenter() {
    return this.center;
  }

  get useWidth() {
    return `${this.width}px`;
  }

  get useHeight() {
    return `${this.height}px`;
  }

  get styles() {
    return `width: ${this.useWidth}; height: ${this.useHeight}`;
  }
}
